/** PROTOTYPE live module breakdown -- presentational. Price or Cost (COGS) from the engine.
 *  Generic over the volume-driven modules (MT, NDT, Coating): onboarding NRE, a per-record
 *  cost-to-price charge, add-ons and floor all render from the shared breakdown shape. */
import React, { useState } from 'react';
import { CRC } from './moduleInputsData';
import { Label, Segmented } from './protoAtoms';
import { SpreadPriceState } from './useSpreadPrice';

const card: React.CSSProperties = { background: '#fff', border: `1px solid ${CRC.border}`, borderTop: `3px solid ${CRC.blueprintBlue}`, borderRadius: 12, boxShadow: '0 1px 3px rgba(0,0,0,0.04)', padding: 20 };
const num = (v: unknown): number => Number((v as number) ?? 0);
const usd = (n: number): string => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const cents = (n: number): string => `${n < 0 ? '-' : ''}$${Math.abs(n).toFixed(2)}`;

interface Comp { label: string; amount: number; cost?: number; detail?: Record<string, unknown>; }

// A per-record charge line carries the shared cost build-up and a record count.
const isPerRecord = (c: Comp): boolean => !!c.detail && c.detail.extractionCompute !== undefined && c.detail.volume !== undefined;
const unit = (d: Record<string, unknown>): string => (d.unit as string) ?? 'pipe';

const Row: React.FC<{ label: string; value: string; note?: string; strong?: boolean; sub?: boolean }> = ({ label, value, note, strong, sub }) => (
  <div style={{ padding: sub ? '2px 0' : '5px 0', borderBottom: sub ? 'none' : `1px solid ${CRC.rowDivider}`, paddingLeft: sub ? 14 : 0 }}>
    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
      <span style={{ fontSize: sub ? 11 : 12, color: strong ? CRC.blueprintBlue : (sub ? CRC.heritageGrey : CRC.textGrey), fontWeight: strong ? 700 : 400 }}>{label}</span>
      <span style={{ fontFamily: 'Funnel Sans', fontSize: sub ? 11.5 : 13, fontWeight: 600, color: strong ? CRC.readableRed : CRC.blueprintBlue }}>{value}</span>
    </div>
    {note && <div style={{ fontSize: 10, color: CRC.heritageGrey, marginTop: 1 }}>{note}</div>}
  </div>
);

// Price-view note: the per-record unit rate (per-record lines) plus the line's realized margin.
const lineNote = (c: Comp): string | undefined => {
  const amount = num(c.amount);
  const parts: string[] = [];
  if (isPerRecord(c) && c.detail) {
    const volume = num(c.detail.volume);
    const net = volume > 0 ? amount / volume : 0;
    parts.push(`${volume.toLocaleString()} ${unit(c.detail)}s x ${cents(net)}/${unit(c.detail)}`);
  }
  if (amount > 0) parts.push(`${Math.round((1 - num(c.cost) / amount) * 100)}% margin`);
  return parts.length ? parts.join('  ·  ') : undefined;
};

// Cost-view note for the onboarding line: the engineer hours x rate behind the NRE cost.
const nreCostNote = (c: Comp): string | undefined => {
  if (!c.label.startsWith('Onboarding') || !c.detail) return undefined;
  const rows = (c.detail.mills ?? c.detail.vendors ?? c.detail.contractors ?? []) as Array<Record<string, unknown>>;
  if (rows.length !== 1) return undefined;
  const m = rows[0];
  return `${num(m.totalHours).toFixed(0)} hrs x ${usd(num(m.engineerRate))}/hr`;
};

// Cost-view per-record build-up: extraction / exceptions / QA / storage -> cost per record.
const perRecordCostRows = (c: Comp) => {
  const d = c.detail as Record<string, unknown>;
  const volume = num(d.volume);
  const costPer = volume > 0 ? num(c.cost) / volume : 0;
  return (
    <>
      <Row sub label="Extraction" value={cents(num(d.extractionCompute))} />
      <Row sub label="Exceptions" value={cents(num(d.exceptionLabour))} />
      <Row sub label="QA overhead" value={cents(num(d.qaOverhead))} />
      <Row sub label="Storage" value={cents(num(d.storage))} />
      <Row sub label={`Cost per ${unit(d)}  x ${volume.toLocaleString()} ${unit(d)}s`} value={cents(costPer)} strong />
    </>
  );
};

interface Props { state: SpreadPriceState; module: 'mt' | 'ndt' | 'coating'; label: string; }

export const SpreadModuleLive: React.FC<Props> = ({ state, module, label }) => {
  const [view, setView] = useState<'price' | 'cost'>('price');
  const result = state.status === 'done' ? state.result[module] : null;
  const d = (result?.details ?? {}) as Record<string, unknown>;
  const oneTime = (d.oneTime ?? {}) as Record<string, unknown>;
  const components = (oneTime.components ?? []) as Comp[];

  const priceRows = () => (
    <>
      {components.map((c, i) => <Row key={i} label={c.label} value={usd(num(c.amount))} note={lineNote(c)} />)}
      <Row label="One-Time Sub-Total" value={usd(num(oneTime.subtotal))} />
      <Row label={`${label} total`} value={usd(result ? result.total : 0)} strong />
    </>
  );

  const costRows = () => (
    <>
      {components.map((c, i) => (
        <React.Fragment key={i}>
          <Row label={c.label} value={usd(num(c.cost))} note={nreCostNote(c)} />
          {isPerRecord(c) && perRecordCostRows(c)}
        </React.Fragment>
      ))}
      <Row label={`${label} cost (COGS)`} value={usd(result ? result.cost : 0)} strong />
    </>
  );

  return (
    <div style={card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div style={{ fontFamily: 'Funnel Sans', fontSize: 15, fontWeight: 600, color: CRC.blueprintBlue }}>{label} breakdown</div>
        {result && (
          <div><Label>View</Label><div style={{ marginTop: 4 }}>
            <Segmented value={view} onChange={(v) => setView(v as 'price' | 'cost')} options={[{ value: 'price', label: 'Price' }, { value: 'cost', label: 'Cost' }]} />
          </div></div>
        )}
      </div>

      <div style={{ marginTop: 14 }}>
        {state.status !== 'done' && state.status !== 'error' && <div style={{ fontSize: 13, color: CRC.heritageGrey }}>Calculating from the pricing engine…</div>}
        {state.status === 'error' && (
          <div style={{ fontSize: 12.5, color: CRC.readableRed, background: 'rgba(196,0,31,0.06)', borderRadius: 8, padding: '10px 12px' }}>
            Could not reach the pricing engine: {state.error}.
          </div>
        )}
        {result && (view === 'price' ? priceRows() : costRows())}
      </div>
    </div>
  );
};
